import React from "react";
import {
	Typography,
	Avatar,
	Modal,
	Drawer,
	Button,
	Space,
	Tooltip
} from "antd";
import {
	RightOutlined,
	SearchOutlined,
	DownOutlined,
	UpOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { accountBaseUrl } from "../../helpers/account";
import { lightGrey } from "../../GlobalConfig";
import { map } from "lodash";
import { trackEvent, setSessionContext, trackButtonClick } from "utils/analytics";
import { isMobile, isDesktop } from "react-device-detect";
import { getPatientType } from "../Booking/reducers";

const DESC_TRUNCATE_LENGTH = 120;
const INITIAL_PILLS_TO_SHOW = 4; // Number of pills to show before "More" button on desktop
const SERVICE_IMAGE_SIZE = 48;

function getInitials(name) {
	if (!name) return "";
	const parts = name.trim().split(/\s+/);
	if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function RenderReasonList({
	reasons,
	availabilityRequests,
	toggleItem,
	themeColor,
	practitionerId,
	multiSelect,
}) {
	const [selectedReasonId, setSelectedReasonId] = React.useState(null);
	const [modalVisible, setModalVisible] = React.useState(false);
	const [descExpanded, setDescExpanded] = React.useState(false);
	const [selectedCategory, setSelectedCategory] = React.useState(null);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [showAllPills, setShowAllPills] = React.useState(false);
	const [searchExpanded, setSearchExpanded] = React.useState(false);
	const searchInputRef = React.useRef(null);

	const history = useHistory();
	const practiceId = useSelector(
		(state) => state.locations.currentLocation?.id,
	);
	const patientType = useSelector((state) => getPatientType(state.booking));

	// Filter by patient visibility first
	const filteredReasonsVisibility = React.useMemo(() => {
		if (patientType === 'new') {
			return reasons.filter((item) =>
				['all', 'new'].includes(item.patientVisibility)
			);
		} else if (patientType === 'existing') {
			return reasons.filter((item) =>
				['all', 'existing'].includes(item.patientVisibility),
			);
		}
		return reasons;
	}, [patientType, reasons]);

	// Helper to get category name from item
	const getCategory = (item) => {
		// Category is an object with a name property
		const cat = item?.category?.name;
		if (cat && typeof cat === 'string' && cat.trim()) {
			return cat.trim();
		}
		return null;
	};

	// Extract unique categories from services
	const categories = React.useMemo(() => {
		const cats = filteredReasonsVisibility
			.map((item) => getCategory(item))
			.filter(Boolean);
		return [...new Set(cats)];
	}, [filteredReasonsVisibility]);

	// Final filtered list based on selected category and search query
	const filteredReasons = React.useMemo(() => {
		let filtered = filteredReasonsVisibility;

		// Filter by category
		if (selectedCategory) {
			filtered = filtered.filter(
				(item) => getCategory(item) === selectedCategory
			);
		}

		// Filter by search query
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			filtered = filtered.filter((item) =>
				item.name?.toLowerCase().includes(query) ||
				item.description?.toLowerCase().includes(query)
			);
		}

		return filtered;
	}, [filteredReasonsVisibility, selectedCategory, searchQuery]);

	const selectedReason = React.useMemo(() => reasons.find((r) => r.id === selectedReasonId), [reasons, selectedReasonId]);
	const filteredReasonPractitioners = React.useMemo(() => {
		if (practitionerId) return selectedReason?.displayPractitioners?.filter(
			(practitioner) => practitioner.id.toString() === practitionerId,
		);
		return selectedReason?.displayPractitioners;
	}, [selectedReason, practitionerId]);

	const handleOpenDrawer = (reasonId) => {
		setSelectedReasonId(reasonId);
		setModalVisible(true);
		setDescExpanded(false);
		const reason = reasons.find((r) => r.id === reasonId);

		// Set service context for all subsequent events
		setSessionContext({
			serviceId: reasonId,
			serviceName: reason?.name,
			servicePrice: reason?.onlineBookingsPrice > 0 ? reason.onlineBookingsPrice : reason?.price,
		});

		trackEvent("service_selected", {
			service_id: reasonId,
			service_name: reason?.name,
			service_price: reason?.onlineBookingsPrice > 0 ? reason.onlineBookingsPrice : reason?.price,
			service_duration: reason?.minutes,
			service_category: reason?.category?.name,
		});
		trackButtonClick('service_card', { service_name: reason?.name });
	};

	const handlePractitionerSelect = React.useCallback((practitioner) => {
		const anyBatch = Date.now();
		if (practitioner.id === "any") {
			map(selectedReason?.displayPractitioners, ({ id }) =>
				toggleItem({
					reasonId: selectedReasonId,
					practitionerId: id,
					anyBatch,
				}),
			);
		} else {
			toggleItem({
				reasonId: selectedReasonId,
				practitionerId: practitioner.id,
				anyBatch: undefined,
			});
		}

		setModalVisible(false);

		// Set practitioner context
		setSessionContext({
			practitionerId: practitioner.id === "any" ? "first_available" : practitioner.id,
			practitionerName: practitioner.name,
		});

		trackEvent("practitioner_selected", {
			practitioner_id: practitioner.id,
			practitioner_name: practitioner.name,
			is_first_available: practitioner.id === "any",
			service_id: selectedReasonId,
			service_name: selectedReason?.name,
		});
		trackButtonClick('practitioner_card', {
			practitioner_name: practitioner.name,
			is_first_available: practitioner.id === "any",
		});

		if (practiceId) {
			history.push(`${accountBaseUrl}/locations/${practiceId}/date`);
		}
	}, [practiceId, selectedReason, toggleItem, selectedReasonId, history]);

	const hasRealDescription = selectedReason?.description &&
		selectedReason.description.trim().toLowerCase() !== selectedReason.name?.trim().toLowerCase();

	const handleCategorySelect = (category) => {
		setSelectedCategory(category);
		trackEvent("category_filter_selected", { category: category || 'all' });
	};

	// Track search with debounce effect
	React.useEffect(() => {
		if (searchQuery.trim()) {
			const timer = setTimeout(() => {
				trackEvent("service_search", {
					search_query: searchQuery.trim(),
					results_count: filteredReasons.length,
				});
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [searchQuery, filteredReasons.length]);

	const descriptionText = selectedReason?.description || "";
	const isDescLong = descriptionText.length > DESC_TRUNCATE_LENGTH;
	const displayDesc = (!isDescLong || descExpanded)
		? descriptionText
		: descriptionText.slice(0, DESC_TRUNCATE_LENGTH) + "...";

	// Check if any services have images (to determine if we should reserve space)
	const anyServiceHasImage = filteredReasons.some((item) => item?.coverPhoto?.url);

	const hasPrice = selectedReason?.showTotalCost && (selectedReason?.onlineBookingsPrice > 0 || selectedReason?.price > 0);
	const priceValue = selectedReason?.onlineBookingsPrice > 0 ? selectedReason.onlineBookingsPrice : selectedReason?.price;

	const modalContent = (
		<div>
			{/* Cover photo */}
			{selectedReason?.coverPhoto?.url && (
				<div style={{ margin: '-24px -24px 0', overflow: 'hidden', maxHeight: 300 }}>
					<img
						style={{
							width: '100%',
							height: 300,
							objectFit: 'cover',
							display: 'block',
						}}
						src={selectedReason.coverPhoto.url}
						alt={selectedReason?.name}
					/>
				</div>
			)}

			{/* Service name - large title */}
			<Typography.Title level={4} style={{
				marginTop: selectedReason?.coverPhoto?.url ? 20 : 0,
				marginBottom: hasRealDescription ? 16 : 20,
			}}>
				{selectedReason?.name}
			</Typography.Title>

			{/* Description/Information in grey box */}
			{hasRealDescription && (
				<div style={{
					marginBottom: 20,
					padding: '14px 16px',
					background: '#f7f7f7',
					borderRadius: 10,
				}}>
					<Typography.Text style={{ fontSize: 14, lineHeight: '22px', color: '#555' }}>
						{displayDesc}
					</Typography.Text>
					{isDescLong && (
						<Typography.Link
							onClick={(e) => {
								e.stopPropagation();
								setDescExpanded(!descExpanded);
							}}
							style={{
								fontSize: 14,
								marginLeft: 4,
								color: themeColor || '#1890ff',
							}}
						>
							{descExpanded ? 'Show less' : 'Read more'}
						</Typography.Link>
					)}
				</div>
			)}

			{/* Practitioner selection */}
			<Typography.Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
				Select a practitioner
			</Typography.Text>

			{[
				{ id: "any", name: "First Available" },
				...(filteredReasonPractitioners || []),
			].map((pract) => (
				<div
					key={pract.id}
					onClick={() => handlePractitionerSelect(pract)}
					className="practitioner-list-item"
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						padding: '14px 12px',
						cursor: 'pointer',
						borderRadius: 10,
						transition: 'background-color 0.15s',
						marginBottom: 2,
						...(pract.id === "any" ? {
							background: themeColor ? `${themeColor}0A` : "#f6f9ff",
							border: `1px solid ${themeColor ? `${themeColor}20` : '#e6ecf5'}`,
						} : {
							border: '1px solid transparent',
						}),
					}}
				>
					{pract.id === "any" ? (
						<Avatar
							icon={<SearchOutlined />}
							style={{
								backgroundColor: themeColor || "#1890ff",
								color: "#fff",
								flexShrink: 0,
							}}
						/>
					) : pract.photo ? (
						<Avatar src={pract.photo} style={{ flexShrink: 0 }} />
					) : (
						<Avatar
							style={{
								backgroundColor: themeColor || "#1890ff",
								color: "#fff",
								flexShrink: 0,
							}}
						>
							{getInitials(pract.name)}
						</Avatar>
					)}
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ fontWeight: pract.id === "any" ? 600 : 500, fontSize: 14 }}>
							{pract.name}
						</div>
						{pract.id === "any" && (
							<Typography.Text type="secondary" style={{ fontSize: 12 }}>
								Recommended
							</Typography.Text>
						)}
					</div>
					<RightOutlined style={{ color: "#bbb", fontSize: 12, flexShrink: 0 }} />
				</div>
			))}

			<style>{`
				.practitioner-list-item:hover {
					background-color: ${lightGrey} !important;
				}
			`}</style>
		</div>
	);

	// Determine which pills to show on desktop
	const visibleCategories = React.useMemo(() => {
		if (isMobile || showAllPills || categories.length <= INITIAL_PILLS_TO_SHOW) {
			return categories;
		}
		return categories.slice(0, INITIAL_PILLS_TO_SHOW);
	}, [categories, showAllPills]);

	const hasMorePills = isDesktop && categories.length > INITIAL_PILLS_TO_SHOW;

	// Focus search input when expanded
	React.useEffect(() => {
		if (searchExpanded && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [searchExpanded]);

	const handleSearchClear = () => {
		setSearchQuery('');
		setSearchExpanded(false);
	};

	return (
		<>
			{/* Category filter pills with search */}
			<div
				style={{
					marginBottom: 20,
					// Mobile: negative margins for edge-to-edge scroll
					marginLeft: isMobile ? -12 : 0,
					marginRight: isMobile ? -12 : 0,
					paddingLeft: isMobile ? 12 : 0,
					paddingRight: isMobile ? 12 : 0,
					// Mobile: horizontal scroll, Desktop: wrap
					overflowX: isMobile ? 'auto' : 'visible',
					WebkitOverflowScrolling: 'touch',
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				}}
				className="category-scroll-container"
			>
				<div style={{
					display: 'flex',
					gap: 8,
					paddingBottom: 4,
					// Desktop: allow wrapping
					flexWrap: isDesktop ? 'wrap' : 'nowrap',
					alignItems: 'center',
				}}>
					{/* Search pill */}
					{searchExpanded ? (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								padding: '10px 18px',
								borderRadius: 24,
								background: '#fff',
								border: `1px solid ${themeColor || '#1890ff'}`,
								flexShrink: 0,
								minWidth: isMobile ? 180 : 220,
							}}
						>
							<SearchOutlined style={{ color: themeColor || '#1890ff', marginRight: 8, fontSize: 14 }} />
							<input
								ref={searchInputRef}
								type="text"
								placeholder="Search..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								onBlur={() => {
									if (!searchQuery.trim()) {
										setSearchExpanded(false);
									}
								}}
								onKeyDown={(e) => {
									if (e.key === 'Escape') {
										handleSearchClear();
									}
								}}
								style={{
									border: 'none',
									outline: 'none',
									flex: 1,
									fontSize: 14,
									background: 'transparent',
								}}
							/>
							{searchQuery && (
								<span
									onClick={handleSearchClear}
									style={{
										cursor: 'pointer',
										color: '#999',
										fontSize: 14,
										padding: '0 4px',
									}}
								>
									✕
								</span>
							)}
						</div>
					) : (
						<div
							onClick={() => setSearchExpanded(true)}
							style={{
								width: 44,
								height: 44,
								cursor: 'pointer',
								borderRadius: 24,
								fontSize: 14,
								fontWeight: 500,
								transition: 'all 0.2s',
								background: '#f5f5f5',
								color: '#666',
								border: 'none',
								flexShrink: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<SearchOutlined style={{ fontSize: 16 }} />
						</div>
					)}

					{/* All category pill */}
					{categories.length > 0 && (
						<div
							onClick={() => handleCategorySelect(null)}
							style={{
								padding: '10px 18px',
								cursor: 'pointer',
								borderRadius: 24,
								fontSize: 14,
								fontWeight: selectedCategory === null ? 600 : 500,
								transition: 'all 0.2s',
								background: selectedCategory === null ? (themeColor || '#000') : '#f5f5f5',
								color: selectedCategory === null ? '#fff' : '#333',
								border: 'none',
								whiteSpace: 'nowrap',
								flexShrink: 0,
							}}
						>
							All
						</div>
					)}
					{/* Category pills */}
					{visibleCategories.map((cat) => {
						const isSelected = selectedCategory === cat;
						return (
							<div
								key={cat}
								onClick={() => handleCategorySelect(cat)}
								style={{
									padding: '10px 18px',
									cursor: 'pointer',
									borderRadius: 24,
									fontSize: 14,
									fontWeight: isSelected ? 600 : 500,
									transition: 'all 0.2s',
									background: isSelected ? (themeColor || '#000') : '#f5f5f5',
									color: isSelected ? '#fff' : '#333',
									border: 'none',
									whiteSpace: 'nowrap',
									flexShrink: 0,
								}}
							>
								{cat}
							</div>
						);
					})}
					{/* More/Less button on desktop */}
					{hasMorePills && (
						<div
							onClick={() => setShowAllPills(!showAllPills)}
							style={{
								padding: '10px 18px',
								cursor: 'pointer',
								borderRadius: 24,
								fontSize: 14,
								fontWeight: 500,
								transition: 'all 0.2s',
								background: '#fff',
								color: themeColor || '#1890ff',
								border: `1px solid ${themeColor || '#1890ff'}`,
								whiteSpace: 'nowrap',
								flexShrink: 0,
								display: 'flex',
								alignItems: 'center',
								gap: 6,
							}}
						>
							{showAllPills ? (
								<>Less <UpOutlined style={{ fontSize: 10 }} /></>
							) : (
								<>+{categories.length - INITIAL_PILLS_TO_SHOW} More <DownOutlined style={{ fontSize: 10 }} /></>
							)}
						</div>
					)}
				</div>
			</div>

			<style>{`
				.category-scroll-container::-webkit-scrollbar {
					display: none;
				}
			`}</style>

			{/* No results message */}
			{filteredReasons.length === 0 && (
				<div style={{ textAlign: 'center', padding: '32px 16px' }}>
					<Typography.Text type="secondary" style={{ fontSize: 15 }}>
						{searchQuery.trim()
							? `No services found matching "${searchQuery}"`
							: 'No services available in this category'}
					</Typography.Text>
					{searchQuery.trim() && (
						<div style={{ marginTop: 12 }}>
							<Button
								type="link"
								onClick={() => {
									setSearchQuery('');
									setSelectedCategory(null);
								}}
							>
								Clear filters
							</Button>
						</div>
					)}
				</div>
			)}

			{filteredReasons.map((item) => (
				<div
					key={item?.id}
					onClick={() => handleOpenDrawer(item.id)}
					style={{
						cursor: "pointer",
						padding: '16px 0',
						borderBottom: '1px solid #f0f0f0',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 12,
					}}
				>
					<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1 }}>
						{/* Always reserve space for image if any service has one */}
						{anyServiceHasImage && (
							<div
								style={{
									width: SERVICE_IMAGE_SIZE,
									height: SERVICE_IMAGE_SIZE,
									borderRadius: 8,
									flexShrink: 0,
									overflow: 'hidden',
									background: (item?.coverPhoto?.url || item?.logo?.url) ? 'transparent' : '#f0f0f0',
								}}
							>
								{(item?.coverPhoto?.url || item?.logo?.url) && (
									<img
										src={item?.coverPhoto?.url || item?.logo?.url}
										alt=""
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								)}
							</div>
						)}
						<div style={{ minWidth: 0, flex: 1 }}>
							<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
								{item.name}
							</div>
							<div style={{ color: '#8c8c8c', fontSize: 13 }}>
								{item.minutes > 0 && <span>{item.minutes} mins</span>}
							</div>
							{(item?.showTotalCost && item.price > 0) && (
								<div style={{ fontWeight: 500, fontSize: 14, marginTop: 4 }}>
									<Space direction={isMobile ? "vertical" : "horizontal"} size={4}>
										<Tooltip title="Estimated fee only. Final fee may vary">
											<Typography.Text style={{ fontWeight: 500, fontSize: 14 }}>
												Fee Estimate: ${item.price}
											</Typography.Text>
										</Tooltip>
										{item?.showDepositCost && ["deposit"].includes(item?.prepaymentType) && item.onlineBookingsPrice > 0 && (
											<Tooltip title="Required deposit to confirm the appointment">
												<Typography.Text type="secondary" style={{ fontWeight: 500, fontSize: 14 }}>
													Deposit: ${item.onlineBookingsPrice}
												</Typography.Text>
											</Tooltip>
										)}
									</Space>
								</div>
							)}
							{(!item?.showTotalCost && ["deposit"].includes(item?.prepaymentType) && item?.showDepositCost && item.onlineBookingsPrice > 0) && (
								<Tooltip title="Required deposit to confirm the appointment" >
									<Typography.Text style={{ fontWeight: 500, fontSize: 14, marginTop: 4 }}>
										Deposit: ${item.onlineBookingsPrice}
									</Typography.Text>
								</Tooltip>
							)}
						</div>
					</div>
					<Button
						shape="round"
						size="middle"
						style={{ flexShrink: 0 }}
					>
						Book
					</Button>
				</div>
			))}

			{isMobile ? (
				<Drawer
					placement="bottom"
					open={modalVisible}
					onClose={() => setModalVisible(false)}
					height="90vh"
					closable
					title={null}
					styles={{
						body: { overflowY: 'auto', padding: 24, paddingBottom: (selectedReason?.minutes > 0 || hasPrice) ? 80 : 24 },
						header: { borderBottom: 'none', padding: '16px 16px 0' },
						wrapper: { borderRadius: '16px 16px 0 0', overflow: 'hidden' },
						footer: { borderTop: '1px solid #f0f0f0', padding: '12px 24px' },
					}}
					style={{ borderRadius: '16px 16px 0 0' }}
					footer={(selectedReason?.minutes > 0 || hasPrice) ? (
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							{selectedReason?.minutes > 0 && (
								<div>
									<Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
										Estimated Time
									</Typography.Text>
									<Typography.Text style={{ fontSize: 15, fontWeight: 500 }}>
										{selectedReason.minutes} mins
									</Typography.Text>
								</div>
							)}
							{hasPrice && (
								<div style={{ textAlign: 'right' }}>
									<Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
										Fee
									</Typography.Text>
									<Typography.Text style={{ fontSize: 15, fontWeight: 600 }}>
										${priceValue}
									</Typography.Text>
								</div>
							)}
						</div>
					) : null}
				>
					{modalContent}
				</Drawer>
			) : (
				<Modal
					open={modalVisible}
					onCancel={() => setModalVisible(false)}
					title={null}
					width={520}
					footer={(selectedReason?.minutes > 0 || hasPrice) ? (
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
							{selectedReason?.minutes > 0 && (
								<div>
									<Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
										Estimated Time
									</Typography.Text>
									<Typography.Text style={{ fontSize: 15, fontWeight: 500 }}>
										{selectedReason.minutes} mins
									</Typography.Text>
								</div>
							)}
							{hasPrice && (
								<div style={{ textAlign: 'right' }}>
									<Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
										Fee
									</Typography.Text>
									<Typography.Text style={{ fontSize: 15, fontWeight: 600 }}>
										${priceValue}
									</Typography.Text>
								</div>
							)}
						</div>
					) : null}
				>
					{modalContent}
				</Modal>
			)}
		</>
	);
}

export default RenderReasonList;
